import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Radio, Popover, Modal, Switch } from 'antd';
import { EditorContext } from '../../hoc/Editor';
import { UserContext } from '../../hoc/User';
import { LocalizationContext } from '../../hoc/Localization';
import { RouteContext } from '../../hoc/Route';
import withRequirement from './withRequirement';
import Select from '../Select';
import Input from '../Input';
import InputNumber from '../Input/InputNumber';
import DatePicker from '../Input/DatePicker';
import TextArea from '../Input/TextArea';
import styles from './styles.less';
import MapSelector from './MapSelector';
import Wysiwyg from './Wysiwyg';
import PriceModal from './PriceModal';
import { toCamelCase } from '../../utils';

let timer;

const InlineEditor = ({
  label,
  type,
  options,
  settings,
  children,
  selectKey,
  adminOnly,
  renderLeft,
  hasTranslations,
  value: defaultValue,
  rows,
  renderOption,
}) => {
  const { edited, editMode: editorEditMode, updateContent, locale } = React.useContext(
    EditorContext,
  );
  const { user } = React.useContext(UserContext);
  const { t, translate, locale: locales } = React.useContext(LocalizationContext);
  const { page } = React.useContext(RouteContext);

  const [modal, setModal] = React.useState(false);
  const [value, setValue] = React.useState(edited[label]);
  const [key, setKey] = React.useState(Date.now());

  React.useEffect(() => {
    if (edited) {
      const translationLabel =
        !hasTranslations || locale === 'default' ? label : `${label}_${locale}`;

      setValue(edited[translationLabel]);
      setKey(Date.now());
    }
  }, [locale]);

  React.useEffect(() => {
    if (
      !['createSchool', 'createCourse'].includes(page) &&
      (!edited || !Object.keys(edited).length)
    ) {
      return;
    }

    const translationLabel =
      !hasTranslations || locale === 'default' ? label : `${label}_${locale}`;

    const content = {
      ...edited,
      [translationLabel]: value,
    };

    if (label === 'Min Group Size' || label === 'Max Group Size') {
      content.Group = true;
    }

    clearTimeout(timer);
    timer = setTimeout(() => {
      updateContent(content);
    }, 250);
  }, [value]);

  const editMode = adminOnly ? user && user.isAdmin && editorEditMode : editorEditMode;
  const multipleDefaultValue =
    Array.isArray(defaultValue) && defaultValue.map(item => item && item.id);

  if (type === 'wysiwyg') {
    if (!editMode) {
      return children || null;
    }

    // eslint-disable-next-line react/jsx-props-no-spreading
    return <Wysiwyg defaultValue={value} onChange={setValue} key={key} {...settings} />;
  }

  if (type === 'text') {
    if (!editMode) {
      return children || null;
    }

    return (
      <div className={styles.alignCenter}>
        {renderLeft()}
        <Input
          version="long"
          defaultValue={value}
          onChange={({ target }) => {
            setValue(target.value);
          }}
          key={key}
          /* eslint-disable-next-line react/jsx-props-no-spreading */
          settings={settings}
        />
      </div>
    );
  }

  if (type === 'textarea') {
    if (!editMode) {
      return children || null;
    }

    return (
      <TextArea
        rows={rows}
        defaultValue={value}
        onChange={({ target }) => {
          setValue(target.value);
        }}
        key={key}
        {...settings}
      />
    );
  }

  if (type === 'select') {
    if (!editMode) {
      return children || null;
    }

    return (
      <Select
        version="default"
        showSearch
        defaultValue={(value && translate(value, selectKey)) || undefined}
        onChange={selectedOption => {
          setValue(options.find(option => option.id === selectedOption));
        }}
        filterOption={(input, option) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
        options={options}
        renderOption={renderOption || (option => translate(option, selectKey))}
        className={styles.select}
        {...settings}
      />
    );
  }

  if (type === 'group') {
    if (!editMode) {
      return children || null;
    }

    return (
      <Radio.Group
        defaultValue={value}
        buttonStyle="solid"
        style={styles.radioButton}
        onChange={({ target }) => {
          setValue(target.value);
        }}
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...settings}
      >
        <Popover
          content={
            <div>
              <div className={styles.groupInput}>
                <span>Min:</span>
                <InlineEditor
                  label="Min Group Size"
                  type="number"
                  value={edited['Min Group Size']}
                  settings={{
                    max: edited['Max Group Size'] || 100,
                    min: 0,
                    placeholder: t('course.edit.group_min.placeholder'),
                  }}
                />
              </div>
              <div className={styles.groupInput}>
                <span>Max:</span>
                <InlineEditor
                  label="Max Group Size"
                  type="number"
                  value={edited['Max Group Size']}
                  settings={{
                    max: 100,
                    min: edited['Min Group Size'] || 0,
                    placeholder: t('course.edit.group_max.placeholder'),
                  }}
                />
              </div>
            </div>
          }
          title="Size"
          trigger="click"
        >
          <Radio.Button className={styles.groupFirst} value>
            Group
          </Radio.Button>
        </Popover>
        <Radio.Button className={styles.groupSecond} value={false}>
          Individual
        </Radio.Button>
      </Radio.Group>
    );
  }

  if (type === 'number') {
    if (!editMode) {
      return children || null;
    }

    // eslint-disable-next-line react/jsx-props-no-spreading
    return (
      <InputNumber
        version="default"
        value={value}
        onChange={setValue}
        className={styles.inputNumber}
        settings={settings}
      />
    );
  }

  if (type === 'location') {
    if (!editMode) {
      return children || null;
    }

    const [initialLocation, setInitialLocation] = React.useState(value || {});

    let address = [];
    if (value) {
      address = [value.city, value.street];
      if (value.house_number) {
        address.push(value.house_number);
        address.push(value.zip_code);
      }
    }

    if (!address.join('')) {
      address[0] = t('school.edit.location.default');
    }

    return (
      <>
        <Modal
          centered
          closable
          footer={null}
          visible={modal}
          onCancel={() => {
            setTimeout(() => {
              setModal(false);
              setValue(initialLocation);
            }, 0);
          }}
          className={styles.mapModal}
        >
          <MapSelector
            value={value}
            setValue={setValue}
            setModal={setModal}
            setInitialLocation={setInitialLocation}
          />
        </Modal>
        <a
          onClick={() => {
            setModal(true);
          }}
        >
          {address.join(' ')}
        </a>
      </>
    );
  }

  if (type === 'range') {
    if (!editMode) {
      return children || null;
    }

    return (
      <div className={styles.range}>
        <InlineEditor
          label={label[0]}
          type="number"
          value={defaultValue ? defaultValue[0] : null}
          settings={settings[0]}
        />
        <InlineEditor
          label={label[1]}
          type="number"
          value={defaultValue ? defaultValue[1] : null}
          settings={settings[1]}
        />
      </div>
    );
  }

  if (type === 'boolean') {
    if (!editMode) {
      return children || null;
    }

    // eslint-disable-next-line react/jsx-props-no-spreading
    return (
      <div>
        <p style={{ display: 'inline-block', marginRight: '5px', marginBottom: 0 }}>
          {t(`course.edit.${toCamelCase(label)}`)}
        </p>
        <Switch checked={value} onChange={setValue} {...settings} />
      </div>
    );
  }

  if (type === 'price') {
    if (!editMode) {
      return children || null;
    }

    return (
      <>
        <a
          className={styles.price}
          onClick={() => {
            setModal(true);
          }}
        >
          {children}
        </a>
        {/* eslint-disable-next-line react/jsx-props-no-spreading */}
        <PriceModal priceModal={modal} setPriceModal={setModal} {...settings} />
      </>
    );
  }

  if (type === 'date') {
    if (!editMode) {
      return children || null;
    }

    return (
      <DatePicker
        // checking `value` because `moment(undefined)` is valid and returns current date
        version="default"
        defaultValue={value && moment(value).isValid() ? moment(value) : null}
        onChange={date => {
          setValue(moment(date).toISOString());
        }}
        /* eslint-disable-next-line react/jsx-props-no-spreading */
        {...settings}
      />
    );
  }

  if (type === 'multiple') {
    if (!editMode) {
      return children || null;
    }

    return (
      <Select
        version="default"
        mode="multiple"
        onChange={e => setValue(e)}
        defaultValue={multipleDefaultValue}
        filterOption={(input, option) =>
          option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
        options={options}
        renderOption={renderOption || (option => option[`title_${locales}`])}
        {...settings}
      />
    );
  }

  return <Input />;
};

InlineEditor.propTypes = {
  type: PropTypes.oneOf([
    'text',
    'date',
    'range',
    'price',
    'group',
    'number',
    'select',
    'wysiwyg',
    'location',
    'textarea',
    'courseImage',
    'multiple',
    'boolean',
  ]),
  value: PropTypes.any,
  rows: PropTypes.number,
  children: PropTypes.any,
  options: PropTypes.array,
  adminOnly: PropTypes.bool,
  renderLeft: PropTypes.func,
  selectKey: PropTypes.string,
  hasTranslations: PropTypes.bool,
  settings: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.array]).isRequired,
  renderOption: PropTypes.func,
};

InlineEditor.defaultProps = {
  value: null,
  options: [],
  settings: {},
  selectKey: '',
  rows: 5,
  type: 'string',
  children: null,
  adminOnly: false,
  renderLeft: () => { },
  hasTranslations: false,
  renderOption: null,
};

export default withRequirement(InlineEditor);
